import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Music2, ChevronLeft, Wand2, Loader2, CheckCircle2, AlertTriangle,
  BookOpen, Image as ImageIcon, Camera, FileText, Guitar, Printer,
} from "lucide-react";

const STYLES = [
  "camp campfire singalong",
  "lullaby",
  "marching tune",
  "sea shanty",
  "upbeat pop",
  "country folk",
  "bluegrass",
  "Caribbean steel-drum",
];

export default function AdminSingAlongImport() {
  const [title, setTitle] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [styleHint, setStyleHint] = useState(STYLES[0]);
  const [characterFocus, setCharacterFocus] = useState("all");
  const [duration, setDuration] = useState(40);
  const [autoCover, setAutoCover] = useState(true);
  const [forcePublish, setForcePublish] = useState(false);
  const [chordChartOn, setChordChartOn] = useState(true);
  const [printableOn, setPrintableOn] = useState(true);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const titleRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => {
    api.get("/characters").then(({ data }) => setCharacters(data || [])).catch(() => {});
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/sing-along/songs");
      setHistory((data || []).slice(0, 12));
    } catch { /* non-fatal */ }
  };

  const reset = () => {
    setTitle("");
    setLyrics("");
    setLastResult(null);
    setForcePublish(false);
    setTimeout(() => titleRef.current?.focus(), 50);
  };

  const onPickPhoto = () => photoRef.current?.click();

  const onPhotoChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-pick same file
    if (!file) return;
    const isPdf = (file.type || "").toLowerCase() === "application/pdf" ||
                  (file.name || "").toLowerCase().endsWith(".pdf");
    if (!file.type.startsWith("image/") && !isPdf) {
      toast.error("Please upload an image (JPG, PNG, HEIC) or a PDF scan.");
      return;
    }
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/sing-along/ocr-lyrics", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const ocrTitle = (data?.title || "").trim();
      const ocrLyrics = (data?.lyrics || "").trim();
      if (!ocrLyrics) {
        toast.error("Couldn't read lyrics from that file. Try a sharper scan.");
        return;
      }
      if (ocrTitle) setTitle(ocrTitle);
      // If there are already lyrics, append the new ones so multi-page photos can stack.
      setLyrics((prev) => (prev.trim() ? `${prev.trim()}\n\n${ocrLyrics}` : ocrLyrics));
      const pageStr = data?.pages_read > 1 ? ` (${data.pages_read} pages)` : "";
      toast.success(`Got it!${pageStr} ${ocrTitle ? `Title: "${ocrTitle}"` : "Lyrics added."}`);
      if (data?.notes) toast.message("Note from the reader", { description: data.notes });
    } catch (err) {
      const detail = err?.response?.data?.detail || "OCR failed";
      toast.error(typeof detail === "string" ? detail : "OCR failed");
    } finally {
      setOcrBusy(false);
    }
  };

  const submit = async () => {
    if (!title.trim() || lyrics.trim().length < 10) {
      toast.error("Add a title and at least a couple of lines of lyrics.");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/sing-along/import-song", {
        title: title.trim(),
        lyrics: lyrics.trim(),
        style_hint: styleHint,
        character_focus: characterFocus,
        duration_seconds: duration,
        auto_cover: autoCover,
        force_publish: forcePublish,
        generate_chord_chart: chordChartOn,
        generate_printable: printableOn,
      });
      setLastResult(data);
      toast.success("Song imported!");
      loadHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Import failed";
      toast.error(typeof detail === "string" ? detail : "Import failed");
      if (typeof detail === "string" && detail.toLowerCase().includes("copyright")) {
        setLastResult({ copyrightWarning: detail });
      }
    } finally {
      setBusy(false);
    }
  };

  const costEstimate = (0.55).toFixed(2); // claude + 11labs music + nano banana

  return (
    <div className="space-y-5" data-testid="admin-sing-along-import">
      <header>
        <Link to="/admin/sing-along" className="text-xs text-[#5a6b76] hover:underline inline-flex items-center gap-1" data-testid="back-to-sing-along">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Sing-Along admin
        </Link>
        <h1 className="font-accent text-3xl font-bold text-[#2e3a3a] flex items-center gap-2 mt-1">
          <BookOpen className="w-7 h-7 text-[#7fcfc7]" /> Import a camp song
        </h1>
        <p className="text-sm text-[#5a6b76] mt-1">
          Snap a photo or paste lyrics → Claude cleans them up + writes the music prompt → ElevenLabs sings it → Nano Banana paints the cover. ~${costEstimate} per song.
        </p>
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LEFT — form */}
        <section className="lg:col-span-2 bg-white border-2 border-[#f4e4c6] rounded-3xl p-5 space-y-4">
          {/* OCR photo uploader */}
          <div className="bg-[#fffbf3] border-2 border-dashed border-[#f0c98a] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1">
                <Camera className="w-3.5 h-3.5" /> Snap or scan a songbook page
              </div>
              <div className="text-xs text-[#8a99a3] mt-0.5">
                Photo (JPG/PNG/HEIC) or scanned PDF — multi-page PDFs are read in order. Upload more files to stack them.
              </div>
            </div>
            <input
              ref={photoRef}
              type="file"
              accept="image/*,application/pdf,.pdf"
              capture="environment"
              className="hidden"
              onChange={onPhotoChosen}
              data-testid="import-photo-input"
            />
            <button
              type="button"
              onClick={onPickPhoto}
              disabled={ocrBusy}
              className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="import-photo-button"
            >
              {ocrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {ocrBusy ? "Reading…" : "Upload photo or PDF"}
            </button>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Title</label>
            <input ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Boom Chicka Boom" maxLength={160}
              className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
              data-testid="import-title" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Raw lyrics (paste from book — typos OK)</label>
            <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={10}
              placeholder={"I said a boom chicka boom\nI said a boom chicka boom\n..."}
              maxLength={8000}
              className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm font-mono"
              data-testid="import-lyrics" />
            <div className="text-[11px] text-[#8a99a3] mt-1">{lyrics.length}/8000 chars</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Style</label>
              <select value={styleHint} onChange={(e) => setStyleHint(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="import-style">
                {STYLES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Featured Sea Star</label>
              <select value={characterFocus} onChange={(e) => setCharacterFocus(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="import-character">
                <option value="all">The whole crew</option>
                {characters.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Duration (sec)</label>
              <input type="number" min={15} max={90} value={duration}
                onChange={(e) => setDuration(Math.max(15, Math.min(90, Number(e.target.value) || 40)))}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="import-duration" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={autoCover} onChange={(e) => setAutoCover(e.target.checked)} className="w-4 h-4 accent-[#7fcfc7]" data-testid="import-cover-toggle" />
              <ImageIcon className="w-4 h-4 text-[#5a8a6f]" /> Auto cover art
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer text-[#a36b29]">
              <input type="checkbox" checked={forcePublish} onChange={(e) => setForcePublish(e.target.checked)} className="w-4 h-4 accent-[#f0a988]" data-testid="import-force-toggle" />
              <AlertTriangle className="w-4 h-4" /> Skip copyright check
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={chordChartOn} onChange={(e) => setChordChartOn(e.target.checked)} className="w-4 h-4 accent-[#7fcfc7]" data-testid="import-chord-toggle" />
              <Guitar className="w-4 h-4 text-[#5a8a6f]" /> Generate guitar chord chart
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={printableOn} onChange={(e) => setPrintableOn(e.target.checked)} className="w-4 h-4 accent-[#7fcfc7]" data-testid="import-printable-toggle" />
              <Printer className="w-4 h-4 text-[#5a8a6f]" /> Generate printable PDF
            </label>
          </div>
          <div className="pt-2 border-t border-[#f4e4c6] flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-[#5a6b76]">⏱ ~30–45 sec to render · 💰 ~${costEstimate}/song</div>
            <button onClick={submit} disabled={busy} className="btn-primary inline-flex disabled:opacity-50" data-testid="import-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {busy ? "Importing…" : "Import song"}
            </button>
          </div>
        </section>

        {/* RIGHT — result + history */}
        <aside className="space-y-3">
          {lastResult?.copyrightWarning && (
            <div className="bg-[#fff4d6] border-2 border-[#f0a988] rounded-3xl p-4 text-xs text-[#a36b29] space-y-1.5" data-testid="import-copyright-warning">
              <div className="font-bold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Possible copyright</div>
              <div>{lastResult.copyrightWarning}</div>
              <div className="pt-1 text-[10px]">If you have permission to publish, tick "Skip copyright check" and try again.</div>
            </div>
          )}
          {lastResult && lastResult.id && (
            <div className="bg-white border-2 border-[#7fcfc7] rounded-3xl p-4 space-y-2" data-testid="import-success">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#5a8a6f]" /><div className="font-bold">{lastResult.title}</div></div>
              {lastResult.cover_image_url && <img src={lastResult.cover_image_url} alt="" className="w-full rounded-2xl border border-[#f4e4c6]" />}
              {lastResult.audio_url && <audio controls src={lastResult.audio_url} className="w-full" />}
              <div className="text-[11px] text-[#5a6b76]"><span className="font-bold">Status:</span> {lastResult.import_meta?.copyright_status}</div>
              <div className="text-[11px] text-[#5a6b76]"><span className="font-bold">Music prompt:</span> {lastResult.import_meta?.music_prompt}</div>
              {lastResult.import_meta?.printable_url && (
                <a
                  href={lastResult.import_meta.printable_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary inline-flex items-center gap-1.5 text-xs w-full justify-center"
                  data-testid="import-printable-link"
                >
                  <FileText className="w-4 h-4" /> Download printable PDF
                </a>
              )}
              {lastResult.import_meta?.chord_chart && (
                <details className="text-[11px] text-[#3a4a55]" data-testid="import-chord-block">
                  <summary className="cursor-pointer font-bold flex items-center gap-1">
                    <Guitar className="w-3.5 h-3.5" /> Guitar chord chart
                  </summary>
                  <pre className="mt-2 p-2 bg-[#fffbf3] border border-[#f4e4c6] rounded-xl overflow-x-auto whitespace-pre font-mono text-[10px] leading-snug">{lastResult.import_meta.chord_chart}</pre>
                </details>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={reset} className="btn-secondary flex-1 justify-center text-xs" data-testid="import-another">Import another</button>
                <Link to="/admin/sing-along" className="btn-ghost text-xs">Open library →</Link>
              </div>
            </div>
          )}
          <div className="bg-white border-2 border-[#f4e4c6] rounded-3xl p-4">
            <h3 className="font-bold text-sm text-[#2e3a3a] mb-2 flex items-center gap-1"><Music2 className="w-3.5 h-3.5" /> Recent songs</h3>
            {history.length === 0 ? (
              <div className="text-xs text-[#8a99a3]">No songs yet — start above!</div>
            ) : (
              <ul className="space-y-1.5">
                {history.map((s) => (
                  <li key={s.id} className="text-xs text-[#3a4a55] truncate flex items-center gap-2" data-testid={`import-history-${s.id}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.active ? "bg-[#5a8a6f]" : "bg-[#cbd5e1]"}`} />
                    {s.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-[#eef9fb] border-2 border-[#7fcfc7] rounded-3xl p-4 text-xs text-[#236f6b] space-y-1.5">
            <div className="font-bold">How copyright works here</div>
            <ul className="list-disc ml-4 space-y-0.5">
              <li>Old traditional / public-domain camp songs (Boom Chicka Boom, Princess Pat, Make New Friends) → safe to publish.</li>
              <li>Songs by known artists (Raffi, Disney, Greg & Steve) → blocked unless you confirm you have permission.</li>
              <li>If the AI is wrong about a classification, tick "Skip copyright check" to override.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
